import axiosClient from '../../api/axiosClient.js';
import { fetchPageData } from '../../api/utils.js';
import AuthService from '../../auth/AuthService';

async function getGeneralInfo() {
  const user = AuthService.getUser();
  const url = user?.is_instructor ? `/api/general-info/` : `/api/student/general-info/`;
  const { data } = await axiosClient.get(url);
  return data;
}

export const generalInfoQuery = () => {
  return {
    queryKey: ['generalInfo'],
    queryFn: async () => getGeneralInfo(),
    refetchOnWindowFocus: false,
    staleTime: Infinity
  };
};

// Trimmed general-info payload for the summary pages. Backend early-
// exits when any of the `?principles / ?classification / ?normal /
// ?balancedequation` params is present, returning only `games`
// (id / game_type / name / scenario) and `sessions` (id / user / game
// / completed / attempt) filtered to the current user + selected game
// type(s). Distinct queryKey so it doesn't clash with the full
// generalInfoQuery cache used by game-play pages.
async function getSummaryGeneralInfo() {
  const user = AuthService.getUser();
  const base = user?.is_instructor ? `/api/general-info/` : `/api/student/general-info/`;
  const { data } = await axiosClient.get(`${base}?balancedequation`);
  return data;
}

export const generalInfoSummaryQuery = () => {
  return {
    queryKey: ['generalInfo', 'summary', 'balancedequation'],
    queryFn: async () => getSummaryGeneralInfo(),
    refetchOnWindowFocus: false,
    staleTime: Infinity
  };
};

async function getConsolidatedScores() {
  const { data } = await axiosClient.get(`/api/scores/`);
  return data;
}

export const consolidatedScoresQuery = () => {
  return {
    queryKey: ['consolidatedScores'],
    queryFn: async () => getConsolidatedScores(),
    refetchOnWindowFocus: false
  };
};

// Trimmed consolidated-scores payload for the summary pages. Always
// scopes to the matching game type (`?balancedequation` here) so the
// response only carries the bucket the page reads. When the caller is
// an instructor, appends `&user=<their_id>` so the endpoint returns
// just the instructor's own scores instead of all students' scores
// across the course. Distinct queryKey (includes the instructor id
// when applicable) so this cache stays separate from
// consolidatedScoresQuery and doesn't cross-pollinate if the same user
// later switches accounts.
async function getSummaryConsolidatedScores() {
  const user = AuthService.getUser();
  const params = ['balancedequation'];
  if (user?.is_instructor && user?.id != null) {
    params.push(`user=${user.id}`);
  }
  const { data } = await axiosClient.get(`/api/scores/?${params.join('&')}`);
  return data;
}

export const consolidatedScoresSummaryQuery = () => {
  const user = AuthService.getUser();
  const scopedUserId = user?.is_instructor ? (user?.id ?? null) : null;
  return {
    queryKey: ['consolidatedScores', 'summary', 'balancedequation', scopedUserId],
    queryFn: async () => getSummaryConsolidatedScores(),
    refetchOnWindowFocus: false
  };
};

async function getConsolidatedInsights({userId} = {}) {
  const url = userId ? `/api/insights/?user=${userId}` : `/api/insights/`;
  const { data } = await axiosClient.get(url);
  return data;
}

export const consolidatedInsightsQuery = ({userId} = {}) => {
  return {
    queryKey: ['consolidatedInsights', userId ?? null],
    queryFn: async () => getConsolidatedInsights({userId}),
    refetchOnWindowFocus: false
  };
};

async function fetchGamesPage(url, allGames = []) {
  const response = await axiosClient.get(url);
  const games = [...allGames, ...response.data.results];
  
  if (response.data.next) {
    return fetchGamesPage(response.data.next, games);
  } else {
    return games;
  }
}

async function getGames() {
  return fetchGamesPage(`/api/games/games/`);
}

export const gamesQuery = () => {
  return {
    queryKey: ['games'],
    queryFn: async () => getGames(),
    refetchOnWindowFocus: false,
    cacheTime: 0
  };
};

async function fetchGamesSessions(url, allSessions = []) {
  const response = await axiosClient.get(url);
  const sessions = [...allSessions, ...response.data.results];
  
  if (response.data.next) {
    return fetchGamesSessions(response.data.next, sessions);
  } else {
    return sessions;
  }
}

export async function getGamesSessions() {
  return fetchGamesSessions(`/api/games/sessions/`);
}

export async function updateGameSession({id, levelNumber, status, isSessionCompleted}) {
  const levelKey = `level${levelNumber}`;
  const requestBody = {
    [levelKey]: status,
  };

  if (isSessionCompleted) {
    requestBody.completed = true;
  }

  const { data } = await axiosClient.patch(`/api/games/sessions/${id}/`, requestBody);

  return data;
}

async function getScenarioLevel({scenarioId, level}) {
  const response = await axiosClient.get(`/api/games/scenario-levels/?scenarios=${scenarioId}&level=${level}`);
  return response.data.results[0];
}

export const scenarioLevelQuery = ({scenarioId, level}) => {
  return {
    queryKey: [`gameScenarioLevel${level}`, scenarioId],
    queryFn: async () => getScenarioLevel({scenarioId, level}),
    refetchOnWindowFocus: false,
    enabled: !!scenarioId
  };
};

async function fetchScenarioLevelsPage(url, allScenarioLevels = []) {
  const response = await axiosClient.get(url);
  const scenarioLevels = [...allScenarioLevels, ...response.data.results];
  
  if (response.data.next) {
    return fetchScenarioLevelsPage(response.data.next, scenarioLevels);
  } else {
    return scenarioLevels;
  }
}

async function getScenarioLevels({scenarioId}) {  
  return scenarioId ? fetchScenarioLevelsPage(`/api/games/scenario-levels/?scenarios=${scenarioId}`) : [];
}

export const scenarioLevelsQuery = ({scenarioId}) => {
  return {
    queryKey: ['gameScenarioLevels', scenarioId],
    queryFn: async () => getScenarioLevels({scenarioId}),
    refetchOnWindowFocus: false,
    staleTime: Infinity
  };
};

async function fetchDecisionsPage(url, allTransactionDecisions = []) {
  const response = await axiosClient.get(url);
  const transactionDecisions = [...allTransactionDecisions, ...response.data.results];
  
  if (response.data.next) {
    return fetchDecisionsPage(response.data.next, transactionDecisions);
  } else {
    return transactionDecisions;
  }
}

export async function getDecisions({userId, gameId, level, gameSessionId}) {
  const params = new URLSearchParams();
  
  if (userId) params.append('user', userId);
  if (gameId) params.append('game', gameId);
  if (level) params.append('level', level);
  if (gameSessionId) params.append('game_session', gameSessionId);
  
  const queryString = params.toString();
  const url = `/api/equation/decisions/${queryString ? `?${queryString}` : ''}`;
  
  return fetchDecisionsPage(url);
}

export const decisionsQuery = ({gameSessionId}) => {
  return {
    queryKey: [`decisionsResponse`],
    queryFn: async () => getDecisions({gameSessionId}),
    enabled: !!gameSessionId,
    refetchOnWindowFocus: false,
    cacheTime: 0
  };
};

export async function makeDecision({
  user,
  game,
  timeElapsed,
  question,
  box,
  itemOption,
  selectedOption,
  selectedChange,
  selectedNormal,
  timestamp,
  groupId,
  gameSessionId
}) {
  const { data } = await axiosClient.post(`api/equation/decisions/`, {
    user,
    game,
    time_elapsed: timeElapsed || 1,
    question,
    box,
    item_option: itemOption,
    selected_change: selectedChange,
    selected_normal: selectedNormal,
    selected_option: selectedOption,
    timestamp,
    group_id: groupId,
    game_session: gameSessionId
  });

  return data;
}

export async function updateDecision({id}) {
  const { data } = await axiosClient.put(`api/equation/decisions/${id}/`, {
    question_answered_correctly: true
  });

  return data;
}

export async function postDecisionsBulk(decisions) {
  const { data } = await axiosClient.post(`api/equation/decisions/bulk/`, decisions);
  return data;
}

export async function getScoresStats({gameId, attempt}) {
  const response = await axiosClient.get(`/api/equation/scores/stats/${gameId}/?attempt=${attempt}`);
  return response.data;
}

export const scoresStatsQuery = ({gameId, attempt}) => {
  return {
    queryKey: ['scoresStats'],
    queryFn: async () => getScoresStats({gameId, attempt}),
    refetchOnWindowFocus: false,
    enabled: !!gameId && !!attempt
  };
};

export async function getScores({gameId, userId, gameSessionId}) {
  return fetchPageData(`/api/equation/scores/?game=${gameId}&user=${userId}&game_session=${gameSessionId}`);
}

export const scoresQuery = ({gameId, userId, gameSessionId}) => {
  return {
    queryKey: [`sessions_${gameSessionId}`],
    queryFn: async () => getScores({gameId, userId, gameSessionId}),
    refetchOnWindowFocus: false,
    enabled: !!gameId && !!userId && !!gameSessionId
  };
};

export async function updateFeedback({id, feedback1, feedback2, feedback3}) {
  const { data } = await axiosClient.patch(`/api/games/sessions/${id}/`, {
    feedback1,
    feedback2,
    feedback3
  });

  return data;
}

export async function getScoresInsights({gameId, gameSessionId}) {
  return fetchPageData(`/api/equation/scores/insights/?game=${gameId}&game_session=${gameSessionId}`);
}

export const scoresInsightsQuery = ({gameId, gameSessionId}) => {
  return {
    queryKey: [`scoresInsights`],
    queryFn: async () => getScoresInsights({gameId, gameSessionId}),
    refetchOnWindowFocus: false,
    enabled: !!gameId && !!gameSessionId
  };
};

export async function getDecisionsInsights({gameId, gameSessionId}) {
  const response = await axiosClient.get(`/api/equation/decisions/insights/${gameId}/${gameSessionId}`);
  return response?.data;
}

export const decisionsInsigtsQuery = ({gameId, gameSessionId}) => {
  return {
    queryKey: [`equationDecisionsInsights`],
    queryFn: async () => getDecisionsInsights({gameId, gameSessionId}),
    enabled: !!gameId && !!gameSessionId,
    refetchOnWindowFocus: false
  }
}

async function getItems() {
  return fetchPageData(`/api/games/items/`);
}

export const itemsQuery = () => {
  return {
    queryKey: ['items'],
    queryFn: async () => getItems(),
    refetchOnWindowFocus: false,
    cacheTime: 0
  };
};

export async function getGamesTransaction({id}) {
  const response = await axiosClient.get(`/api/games/transactions/${id}/`);
  return response;
}


export async function getTournamentsRounds() {
  return fetchPageData(`/api/tournaments/rounds/`);
}

export const tournamentsRoundsQuery = () => {
  return {
    queryKey: ['tournamentsRoundsClassification'],
    queryFn: async () => getTournamentsRounds(),
    refetchOnWindowFocus: false
  };
};

async function getCourses() {
  return fetchPageData(`/api/users/courses/`);
}

export const coursesQuery = () => {
  return {
    queryKey: ['balancedEquationCourses'],
    queryFn: async () => getCourses(),
    refetchOnWindowFocus: false
  };
};

async function getScenarioLevelsView(scenarioId) {
  const response = await axiosClient.get(`/api/games/scenario-levels/view/?scenarios=${scenarioId}`);
  return response.data;
}

export const scenarioLevelsViewQuery = (scenarioId) => {
  return {
    queryKey: ['scenarioLevelsView', scenarioId],
    queryFn: async () => getScenarioLevelsView(scenarioId),
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    enabled: !!scenarioId
  };
};